import { useEffect, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { Loader, Tooltip } from '@mantine/core'

import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_KNOWLEDGE_BASE,
  GET_KNOWLEDGE_BASE,
} from 'src/graphql/knowledgeBases.graphql'
import IconInfoCircle from 'src/icons/IconInfoCircle'
import { cn } from 'src/utils'

import SettingsSectionCard from '../../../components/SettingsSectionCard'
import { KNOWLEDGE_BASE_MAX_LENGTH } from '../lib/knowledgeBaseMarkdown'

import KnowledgeBaseEditor from './KnowledgeBaseEditor'

const KnowledgeBaseCard = ({ voiceAgent }) => {
  const [editing, setEditing] = useState(false)
  const [kbText, setKbText] = useState('')

  const {
    data: knowledgeBaseData,
    loading,
    refetch: refetchKnowledgeBase,
  } = useQuery(GET_KNOWLEDGE_BASE, {
    variables: { voiceAgentId: voiceAgent.voiceAgentId },
    skip: !voiceAgent.voiceAgentId,
  })

  const savedText = knowledgeBaseData?.knowledgeBase?.text || ''

  // Sync internal state with fetched data only when not editing
  useEffect(() => {
    if (!editing) {
      setKbText(savedText)
    }
  }, [savedText, editing])

  const [createKnowledgeBase, { loading: saving }] = useMutation(
    CREATE_KNOWLEDGE_BASE,
    {
      onCompleted: () => {
        toast('Knowledge base updated successfully', 'success')
        setEditing(false)
        refetchKnowledgeBase()
      },
      onError: (error) => {
        toast(`Failed to update knowledge base: ${error.message}`, 'error')
      },
    }
  )

  const handleSave = () => {
    createKnowledgeBase({
      variables: {
        input: {
          voiceAgentId: voiceAgent.voiceAgentId,
          text: kbText,
        },
      },
    })
  }

  const handleCancel = () => {
    setKbText(savedText)
    setEditing(false)
  }

  const characterCount = kbText.length
  const overLimit = characterCount > KNOWLEDGE_BASE_MAX_LENGTH

  return (
    <SettingsSectionCard
      title={
        <div className="flex flex-row items-center gap-1">
          Knowledge Base
          <Tooltip
            label="Information the voice agent uses to answer questions about your company, such as pay, benefits, routes, and equipment."
            multiline
            w={320}
          >
            <div className="flex items-center">
              <IconInfoCircle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
            </div>
          </Tooltip>
        </div>
      }
      editable
      editing={editing}
      onEdit={() => setEditing(true)}
      onSave={handleSave}
      onCancel={handleCancel}
      saveDisabled={!kbText.trim() || kbText === savedText || overLimit}
      loading={saving}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center justify-end">
          <div
            className={cn(
              'whitespace-nowrap text-xs',
              overLimit
                ? 'font-medium text-doubleNickel-error-500'
                : 'text-doubleNickel-gray-500'
            )}
          >
            {characterCount.toLocaleString()} /{' '}
            {KNOWLEDGE_BASE_MAX_LENGTH.toLocaleString()} characters
          </div>
        </div>
        {overLimit && (
          <div className="text-xs text-doubleNickel-error-500">
            The knowledge base exceeds the maximum length. Shorten the content
            to save your changes.
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader color="gray" size="xs" />
          </div>
        ) : (
          <KnowledgeBaseEditor
            key={`${editing}-${savedText}`}
            value={kbText}
            onChange={setKbText}
            editable={editing && !saving}
          />
        )}
      </div>
    </SettingsSectionCard>
  )
}

export default KnowledgeBaseCard
